import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { QUOTE } from '../../constants';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Dim resting state and the neon-red filled state of each character
const CHAR_DIM = 'rgba(245,243,238,0.13)';
const CHAR_LIT = '#ff2d00';
const GLOW_OFF = '0 0 0px rgba(255,45,0,0)';
const GLOW_ON = '0 0 18px rgba(255,45,0,0.45)';

const WORDS = QUOTE.split(' ');

/**
 * Black full-bleed statement section — characters fill left-to-right
 * with a neon-red glow as you scroll down, and drain back on scroll up
 * (scrub-tied, so it runs both directions).
 */
const Quote: React.FC = () => {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const chars = gsap.utils.toArray<HTMLElement>('.q-char');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(chars, { color: CHAR_LIT, textShadow: GLOW_OFF });
        return;
      }

      gsap.fromTo(
        chars,
        { color: CHAR_DIM, textShadow: GLOW_OFF },
        {
          color: CHAR_LIT,
          textShadow: GLOW_ON,
          ease: 'none',
          stagger: 0.03,
          scrollTrigger: {
            trigger: '.q-block',
            start: 'top 78%',
            end: 'bottom 45%',
            scrub: 0.4,
          },
        }
      );

      gsap.fromTo(
        '.quote-photo',
        { yPercent: -10 },
        {
          yPercent: 10,
          ease: 'none',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
          },
        }
      );
      // Photo and name pop in once the signature block enters
      gsap.fromTo(
        '.quote-photo',
        { scale: 0.55, autoAlpha: 0 },
        {
          scale: 1,
          autoAlpha: 1,
          duration: 1,
          ease: 'back.out(1.5)',
          scrollTrigger: { trigger: '.quote-sign', start: 'top 92%', once: true },
        }
      );
      gsap.fromTo(
        '.quote-name',
        { y: 26, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.8,
          ease: 'power3.out',
          delay: 0.25,
          scrollTrigger: { trigger: '.quote-sign', start: 'top 92%', once: true },
        }
      );
    },
    { scope: ref }
  );

  return (
    <section ref={ref} className='bg-carbon text-chalk'>
      <div className='max-w-5xl mx-auto px-5 sm:px-6 py-24 sm:py-36 text-center'>
        <div className='label-mono text-chalk/40 mb-10 flex items-center justify-center gap-2'>
          <span className='text-accent'>[</span> A note from me{' '}
          <span className='text-accent'>]</span>
        </div>

        <blockquote className='q-block font-serif leading-[1.15] text-[clamp(1.7rem,4.2vw,3.4rem)]'>
          {WORDS.map((word, wi) => (
            <React.Fragment key={wi}>
              <span className='inline-block'>
                {Array.from(word).map((ch, ci) => (
                  <span key={ci} className='q-char' style={{ color: CHAR_DIM }}>
                    {ch}
                  </span>
                ))}
              </span>
              {wi < WORDS.length - 1 ? ' ' : ''}
            </React.Fragment>
          ))}
        </blockquote>

        <div className='quote-sign mt-16 flex flex-col items-center justify-center gap-6'>
          <div className='quote-photo w-32 h-32 sm:w-44 sm:h-44 rounded-full overflow-hidden border-2 border-chalk/25 shadow-[0_0_60px_rgba(255,255,255,0.08)] will-change-transform'>
            <img src='/Assets/Photo.jpg' alt='Akash Roy' className='w-full h-full object-cover' />
          </div>
          <div className='quote-name font-medium tracking-tight text-3xl sm:text-4xl'>
            Akash Roy
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quote;
